import React from "react"
import ReactDOM from "react-dom"
import classNames from "classnames"
import {
    // position as caretPosition,
    offset as caretOffset,
} from "caret-pos"
import { connect } from "react-redux"
import TextareaAutosize from "react-textarea-autosize"
import { Textarea } from "@mantine/core"

import { atMentionConnection } from "shared/userdata/userdata-selectors"
import Select from "app/static/frontend/selects/components/Select"

import AtMentionMixin from "shared/components/AtMentionMixin"

const getSelectPositionDefault = ({ left, top, textarea, allowExpandUp, cursorLocation }) => {
    if (left === undefined && textarea) {
        const pos = $(".at-mentionable").offset()
        const contentEditableLineHeight =
            textarea && !allowExpandUp ? parseInt(window.getComputedStyle(textarea).getPropertyValue("line-height")) : 0
        const offset = caretOffset(textarea, cursorLocation)

        const adjustedTop = offset.top - pos.top + contentEditableLineHeight + "px"
        const adjustedLeft = offset.left - pos.left + "px"
        return { left: adjustedLeft, top: adjustedTop }
    }

    return { left, top }
}

export const UnconnectedAtMentionSelect = React.forwardRef((props, ref) => {
    let { left, top } = props.style || {}
    const { getSelectPosition = getSelectPositionDefault } = props

    const position = getSelectPosition({
        left,
        top,
        textarea: props.textarea,
        allowExpandUp: props.allowExpandUp,
        cursorLocation: props.cursorLocation,
    })

    return (
        <span className="mention-select" style={position}>
            {props.teamMembers ? (
                <Select
                    async={false}
                    defaultMenuIsOpen
                    isFocused
                    menuIsOpen
                    requiresBaseComponent
                    inputValue={props.filter}
                    onChange={props.onSubmit}
                    parentRef={ref}
                    loadOptions={() =>
                        props.teamMembers.map((each) => ({
                            label: each.name,
                            value: (each.name + each.username + each.email).toLowerCase(),
                            ...each,
                        }))
                    }
                    onKeyDown={(e) => {
                        e.stopPropagation()
                        e.nativeEvent.stopImmediatePropagation()
                    }}
                    allowExpandUp={props.allowExpandUp}
                />
            ) : (
                <span className="loading">Loading...</span>
            )}
        </span>
    )
})

export const AtMentionSelect = connect(atMentionConnection, {})(UnconnectedAtMentionSelect)

export class AtMentionTextarea extends AtMentionMixin {
    constructor(props) {
        super(props)
        this.select = React.createRef()
        this.textarea = React.createRef()
    }
    state = {
        cursorLocation: 0,
        mentioning: false,
        mentionFilter: "",
        mentionTags: {},
    }
    componentDidMount = () => {
        // once we load a pre-existing note, we should fill up the mentionTags object, which is used in the stored->display and
        // display->stored transformation of the text
        const value = this.props.value
        this.loadMentionTags(value)
    }
    componentDidUpdate = (prevProps) => {
        if (prevProps.value !== this.props.value) {
            const value = this.props.value
            this.loadMentionTags(value)
        }
        // because we keep on setting state, which in turn refreshes the textarea, it forgets where the cursor is supposed
        // to be at times. Since we keep track of where the cursor is meant to be, every update we reset the cursor to its
        // proper location
        this.setCursor()
    }
    setCursor = () => {
        if (this.props.useMantineTextarea) {
            this.textarea &&
                this.textarea.current?.setSelectionRange(this.state.cursorLocation, this.state.cursorLocation)
        } else {
            this.textarea && this.textarea.setSelectionRange(this.state.cursorLocation, this.state.cursorLocation)
        }
    }
    onKeyDown = (e) => {
        // if the user presses the 37 (left) or 39 (right) key while the select is active,
        // ignore it since we only want them scrolling through the select
        if ([39, 37].includes(e.keyCode) && this.select.current) {
            e.preventDefault()
            return false
        }

        // if the user presses the 27 (esc) key, close the select
        else if (e.keyCode === 27) {
            this.setState({ mentioning: false })
            e.stopPropagation()
        }

        // keyCodes: 40 (down), 48 (up), 13 (enter)
        else if (this.state.mentioning && [40, 38, 13].includes(e.keyCode)) {
            // we don't want the cursor to move to the beginning or end while we scroll
            e.preventDefault()
            e.stopPropagation()
            e.nativeEvent.stopImmediatePropagation()

            const selectDomNode = ReactDOM.findDOMNode(this.select.current)
            const selectInput = selectDomNode && selectDomNode.getElementsByClassName("Select__input")[0]

            // fire the react-select input onKeyDown to scroll
            // through the select without actually focusing it
            if (selectInput) {
                selectInput.dispatchEvent(new KeyboardEvent("keydown", e))
            }
        }
    }

    _callOnChange = (value) => {
        return this.props.onChange(value)
    }
    _getCursorLocation = (e) => {
        return e.target.selectionStart
    }
    _formatOnChangeInput = (value, e) => {
        e.target.value = value
        return e
    }
    wrapOnChange = (onChange, currentValue) => (e) => {
        return this.handleAtMentionOnChange({ onChange, currentValue, e, newValue: e.target.value })
    }
    addEmptyAt = (currentValue) => () => {
        const toAdd = currentValue && ![" ", "\n"].includes(currentValue.slice(-1)) ? " @" : "@"
        const toPlace = currentValue.length + toAdd.length + 1
        this.props.onChange(this.transformToStore(currentValue + toAdd))
        this.setState({
            mentioning: true,
            cursorLocation: toPlace,
            toPlaceAnchor: toPlace,
        })
        $(".at-mentionable").focus()
    }

    render() {
        const {
            disabled,
            name,
            onChange,
            value,
            placeholder = "",
            className = "",
            textareaProps = {},
            displayAtButton = true,
            withAsterisk,
            label,
            error,
            id,
            description,
            selectProps = {},
        } = this.props

        const { cursorLocation, mentioning, mentionFilter, toPlaceAnchor } = this.state

        const TextArea = this.props.useMantineTextarea ? Textarea : TextareaAutosize

        let toDisplayValue = this.transformToDisplay(value)

        return (
            <span>
                <TextArea
                    name={name}
                    disabled={disabled}
                    value={toDisplayValue}
                    onKeyDown={this.onKeyDown}
                    ref={this.textarea}
                    inputRef={(inst) => {
                        if (!this.props.useMantineTextarea) {
                            this.textarea = inst
                        }
                    }}
                    onChange={this.wrapOnChange(onChange, toDisplayValue)}
                    className={`${className} at-mentionable`}
                    placeholder={placeholder || "Write a comment... Use the @ sign to mention team members"}
                    {...textareaProps}
                    label={label}
                    withAsterisk={withAsterisk}
                    error={error}
                    id={id}
                    description={description}
                />
                {mentioning && (
                    <AtMentionSelect
                        filter={mentionFilter}
                        cursorLocation={toPlaceAnchor}
                        onSubmit={this.insertMention(toDisplayValue)}
                        textarea={this.props.useMantineTextarea ? this.textarea.current : this.textarea}
                        ref={this.select}
                        {...selectProps}
                    />
                )}
                {displayAtButton && (
                    // ignoring jsx-a11y rules to apply other style changes to the file - should be updated to enforce accessibility
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                    <div
                        onClick={this.addEmptyAt(toDisplayValue)}
                        className={classNames({
                            "at-mention-helper": true,
                            "mantine-textarea": this.props.useMantineTextarea,
                            DS: true,
                            mentioning: mentioning,
                        })}
                    >
                        @
                    </div>
                )}
            </span>
        )
    }
}

export default AtMentionTextarea
